import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useLocation, useNavigate } from 'react-router-dom';

import {
  ACCESS_TIERS,
  getAccessPolicy,
  getAccessUpgradeCopy,
  hasAccessForTier,
} from '../configs/accessPolicies';
import { startSubscriptionCheckout, openCustomerPortal } from '../utils/billing';
import useSubscriptionStatus from './useSubscriptionStatus';
import { trackEvent } from '../utils/analytics';

export default function useAccessGate({ policyId, requiredTier: requiredTierOverride } = {}) {
  const location = useLocation();
  const navigate = useNavigate();
  const { getAccessTokenSilently, loginWithRedirect } = useAuth0();
  const entitlements = useSubscriptionStatus();
  const policy = getAccessPolicy(policyId, requiredTierOverride);
  const requiredTier = requiredTierOverride || policy.requiredTier;
  const [upgradeError, setUpgradeError] = useState(null);
  const trackedGate = useRef(null);
  const allowed = hasAccessForTier({ requiredTier, ...entitlements });
  const copy = useMemo(
    () => getAccessUpgradeCopy(requiredTier, entitlements.isAuthenticated),
    [entitlements.isAuthenticated, requiredTier]
  );

  useEffect(() => {
    if (!allowed) {
      const gateKey = `${policyId || 'access_gate'}:${requiredTier}`;
      if (trackedGate.current !== gateKey) {
        trackedGate.current = gateKey;
        trackEvent('access_gate_view', {
          access_level: requiredTier,
          signed_in: entitlements.isAuthenticated,
          cta_location: policyId || 'access_gate',
        });
      }
    }
  }, [allowed, entitlements.isAuthenticated, policyId, requiredTier]);

  const upgrade = useCallback(async () => {
    const returnTo = `${location.pathname}${location.search || ''}`;
    setUpgradeError(null);
    trackEvent('upgrade_click', {
      access_level: requiredTier,
      signed_in: entitlements.isAuthenticated,
      cta_location: policyId || 'access_gate',
    });

    try {
      if (!entitlements.isAuthenticated) {
        const requestedTier = requiredTier === ACCESS_TIERS.RESEARCH ? 'research' : 'trial';
        localStorage.setItem('redirectTo', returnTo);
        localStorage.setItem('requestedTier', requestedTier);
        await loginWithRedirect({
          appState: { requestedTier },
          authorizationParams: { screen_hint: 'signup', scope: 'openid email profile' },
        });
        return;
      }

      if (requiredTier === ACCESS_TIERS.RESEARCH && entitlements.hasCoreAccess) {
        await openCustomerPortal(getAccessTokenSilently);
        return;
      }

      if (requiredTier === ACCESS_TIERS.AUTHENTICATED) {
        navigate(returnTo);
        return;
      }

      await startSubscriptionCheckout(getAccessTokenSilently, {
        mode: requiredTier === ACCESS_TIERS.CORE ? 'trial' : 'pay',
        subscriptionTier: requiredTier,
      });
    } catch (error) {
      console.error('Unable to start subscription upgrade', error);
      setUpgradeError(error);
    }
  }, [
    entitlements.hasCoreAccess,
    entitlements.isAuthenticated,
    getAccessTokenSilently,
    location.pathname,
    location.search,
    loginWithRedirect,
    navigate,
    requiredTier,
    policyId,
  ]);

  return {
    ...entitlements,
    ...copy,
    allowed,
    gateActive: !allowed,
    policy,
    requiredTier,
    upgrade,
    upgradeError,
  };
}
